import { useRef, useEffect, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import { Card, CardContent } from '@/components/ui/card';
import { testimonials, projects } from '@/data/portfolio';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StarIcon } from './icons/StarIcon';
import clsx from 'clsx';

export const TestimonialsSection = () => {
  const [filteredTestimonials, setFilteredTestimonials] = useState(testimonials);
  
  const handleFilter = (projectId: string) => {
    if (projectId === "all") {
      setFilteredTestimonials(testimonials);
      return;
    }
    setFilteredTestimonials(testimonials.filter(t => t.projectId?.toString() === projectId));
  };

  if (!testimonials.length) return null;
  
  const swiperRef = useRef<any>(null);
  
  useEffect(() => {
    if (!swiperRef.current) return;
    
    const swiper = swiperRef.current.swiper;
    
    swiper.on('reachEnd', () => {
      setTimeout(() => {
        swiper.slideTo(0, 0);
        if (swiper.autoplay?.running === false) {
          swiper.autoplay?.start();
        }
      }, 1500);
    });
    
    return () => {
      swiper.off('reachEnd');
    };
  }, []);

  return (
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center mb-12">
          <h2 className="text-2xl md:text-3xl text-gray-700 leading-relaxed mb-8">Client Testimonials</h2>
          <Select onValueChange={handleFilter}>
            <SelectTrigger className="w-[200px] bg-gray-100 border-gray-300">
              <SelectValue placeholder="Filter by project" />
            </SelectTrigger>
            <SelectContent className="bg-gray-100 border-gray-300">
              <SelectItem value="all">All Projects</SelectItem>
              {projects.map(project => (
                <SelectItem key={project.id} value={project.id.toString()}>
                  {project.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Swiper
          ref={swiperRef}
          modules={[Autoplay, Pagination]}
          spaceBetween={16}
          slidesPerView={1}
          autoplay={{
            delay: 1500,
            disableOnInteraction: false
          }}
          pagination={{
            clickable: true,
            dynamicBullets: true
          }}
          loop={false}
          className="!pb-8"
        >
          {filteredTestimonials.map((testimonial, index) => (
            <SwiperSlide key={index}>
              <Card className="border-gray-200 bg-white relative overflow-hidden group hover:shadow-lg transition-all duration-300">
                <CardContent className="p-6 relative z-10 bg-white p-8 md:p-10 rounded-2xl shadow-md border border-gray-200">
                  <div className="flex items-start gap-4">
                    <div className="relative w-12 h-12 flex-shrink-0 mt-1">
                      <div className="absolute inset-0 bg-gradient-to-tr from-purple-600 to-blue-500 rounded-full flex items-center justify-center text-white font-bold shadow-md">
                        {testimonial.author.split(' ').map(n => n[0]).join('')}
                      </div>
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <p className={clsx("text-sm text-gray-700")}>{testimonial.author}</p>
                          <p className={clsx("text-sm text-gray-700")}>{testimonial.position}</p>
                        </div>
                        {testimonial.projectId && (
                          <span className="text-xs bg-gray-100 px-2 py-1 rounded-full text-primary">
                            Project #{testimonial.projectId}
                          </span>
                        )}
                      </div>
                      
                      <p className="text-gray-700 italic mb-4 relative pl-4">
                        <span className="absolute left-0 top-0 text-3xl text-gray-800 select-none">"</span>
                        {testimonial.text}
                        <span className="absolute right-0 bottom-0 text-3xl text-gray-800 select-none">"</span>
                      </p>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex gap-1">
                          {[...Array(5)].map((_, i) => (
                            <StarIcon 
                              key={i} 
                              className={`w-4 h-4 ${i < testimonial.rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`}
                            />
                          ))}
                        </div>
                        <div className="text-xs text-gray-600">
                          {testimonial.date ? new Date(testimonial.date).toLocaleDateString() : null}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </section>
  );
};
